// @flow

import * as React from 'react';
import * as TripTypes from '@ta/trips.trip-types';
import Modal from '@ta/modal.styleguide';
import { TitleBar } from '@ta/modal.headers';
import { LoadingSpinner } from '@ta/styleguide.loading';
import { toSavesObject } from '@ta/trips.trip-util';
import Localize from '@ta/platform.i18n';
import type { TripsScopedError } from '@ta/trips.trip-errors';
import type {
  CreateTripWithAddItemModalProps,
  CreateTripWithMoveItemModalProps,
} from '@ta/trips.create-trip-modal';
import type { SaveToTripContentProps } from '@ta/trips.save-to-trip-modal';
import { featureIsEnabled } from '@ta/common.features';
import * as Import from '@ta/platform.import';
import type { ReferenceType } from '@ta/trips.trip-types';
import { TripsImpression } from '@ta/trips.impressions';
import {
  useTripInteraction,
  type TripsContext,
  type TripInteractionSources,
} from '@ta/trips.tracking';
import LoadingErrorMessage from '@ta/core-ui.loading-error';

import useUserTrips from './use-user-trips';
import styles from './STATModal.css';

export type OverlayState = 'DEFAULT' | 'CREATE' | 'SELECT' | 'SELECT_CREATE';

export type ItemAction = 'ADD' | 'MOVE' | 'REMOVE';
export type TripType = 'NEW' | 'EXISTING';

export type STATCallback = (
  itemAction: ItemAction,
  tripType: TripType,
  trip: TripTypes.Trip,
  saved: boolean,
  isActive: boolean,
) => void;

export type STATProps = {|
  /**
   * The Save Object representation of the thing we want to save
   */
  saveObject: TripTypes.TripSaveObject,

  /**
   * The overlay state we should show the user
   */
  overlayState: OverlayState,

  preventInteractionInMountedComponents: boolean,

  /**
   * onclose handler
   */
  onClose: () => void,

  /**
   * the last trip item we created.
   */
  lastSavedItem: ?TripTypes.TripItem,

  /**
   * Context to use for tracking
   */
  trackingContext?: TripsContext,

  /**
   * Source to use for tracking
   * */
  trackingSource?: TripInteractionSources,

  /**
   * Callback when the user clicks 'create a trip'
   */
  onCreateTripClicked: () => void,

  /** Trip name validator: new trip name -> error message or null if validation succeeds */
  validateTripName?: string => ?string,

  /**
   * Callback when the user clicks on a POI and selects to create new trip
   */
  onSelectCreateTripClicked: () => void,

  /**
   * Callback when the user made an action on the selected trip item.
   */
  statCallback: STATCallback,

  onError?: TripsScopedError => void,
|};

/* eslint-disable react/display-name */
const OverlayStateTitle: { [string]: (ReferenceType) => React.Node } = Object.freeze({
  DEFAULT: (type: ReferenceType) =>
    type !== 'bundleSave' ? (
      /* Save to a Trip */
      <Localize id="stat_modal_save_to_trip_v2" />
    ) : (
      /* Save all items to a Trip */
      <Localize id="save_all_items_modal_header" />
    ),
  /* Create a Trip */
  CREATE: () => <Localize id="stat_modal_create_a_trip_v2" />,
  /* Select a Trip */
  SELECT: () => <Localize id="select_trip_header" />,
  /* Create a Trip */
  SELECT_CREATE: () => <Localize id="stat_modal_create_a_trip_v2" />,
});
/* eslint-enable react/display-name */

const CreateTripWithAddItemModal = Import.component.onUsage<_, CreateTripWithAddItemModalProps>(
  () => import('@ta/trips.create-trip-modal'),
  'CreateTripWithAddItemModal',
  <div className={styles.loadingContainer}>
    <LoadingSpinner size="large" />
  </div>,
);

const CreateTripWithMoveItemModal = Import.component.onUsage<_, CreateTripWithMoveItemModalProps>(
  () => import('@ta/trips.create-trip-modal'),
  'CreateTripWithMoveItemModal',
  <div className={styles.loadingContainer}>
    <LoadingSpinner size="large" />
  </div>,
);

const SaveToTripContent = Import.component.onUsage<_, SaveToTripContentProps>(
  () => import('@ta/trips.save-to-trip-modal'),
  'SaveToTripContent',
  <div className={styles.loadingContainer}>
    <LoadingSpinner size="large" />
  </div>,
);

const STATModal = ({
  saveObject,
  overlayState,
  validateTripName,
  lastSavedItem,
  trackingContext,
  trackingSource,
  ...props
}: STATProps) => {
  const [splashLoad, setSplashLoad] = React.useState(true);

  const { fetching, error, data, hasMore, loadMore } = useUserTrips(saveObject);
  let loadingNode;
  if (fetching) {
    loadingNode = <LoadingSpinner size="large" />;
  } else if (error) {
    loadingNode = <LoadingErrorMessage refetch={loadMore} />;
  }

  React.useEffect(() => {
    if (!fetching) {
      setSplashLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [logInteraction] = useTripInteraction({
    source: trackingSource || 'trips',
    trackingContext,
    pageAsContext: true,
    withSaveSession: true,
  });

  const closeModal = () => {
    props.onClose();
    setSplashLoad(true);
  };

  const title = OverlayStateTitle[overlayState](saveObject.type);
  const modalHeader = (
    <TitleBar
      iconName={featureIsEnabled('trips_heart_icon') ? 'heart' : 'suitcase'}
      displayType="LEFT_ALIGN_GRAY"
    >
      {title}
    </TitleBar>
  );

  const content = (): React.Node => {
    const lastAutoSavedObject = lastSavedItem ? toSavesObject(lastSavedItem) : undefined;

    const createContent = (): React.Node => (
      <div className={styles.modalPadding}>
        <CreateTripWithAddItemModal
          gate={() => Promise.resolve() /* do not propagate arguments */}
          object={saveObject}
          onMount={() => {
            /*
              We check that userTripData is loaded, to avoid to log the event twice.
             */
            if (!fetching && !error) {
              logInteraction({ elementClick: { element: 'createTrip' } });
            }
          }}
          onAddedToCreatedTrip={(trip: TripTypes.Trip) => {
            props.statCallback('ADD', 'NEW', trip, true, true);
            closeModal();
          }}
          onError={props.onError}
          canMakePublicTrip={data.canMakePublicTrip}
          validateTripName={validateTripName}
          trackingContext={trackingContext}
          trackingSource={trackingSource}
        />
      </div>
    );

    switch (overlayState) {
      case 'DEFAULT':
        return data.tripList.length === 0 ? (
          createContent()
        ) : (
          <SaveToTripContent
            tripList={data.tripList}
            savedSet={data.saveSet}
            tripListBottom={loadingNode}
            onPaginate={loadMore}
            hasMore={hasMore}
            saveObject={saveObject}
            onMount={() => {
              if (!props.preventInteractionInMountedComponents) {
                logInteraction({ elementClick: { element: 'stat' } });
              }
            }}
            onCreateTrip={() => {
              if (props.onCreateTripClicked) {
                props.onCreateTripClicked();
              }
            }}
            tripAction="SAVE"
            onRemovedFromTrip={(trip: TripTypes.Trip, saved: boolean, isActive: boolean) => {
              props.statCallback('REMOVE', 'EXISTING', trip, saved, isActive);
              closeModal();
            }}
            onSavedToTrip={(trip: TripTypes.Trip, saved: boolean, isActive: boolean) => {
              props.statCallback('ADD', 'EXISTING', trip, saved, isActive);
              closeModal();
            }}
            onError={props.onError}
            trackingContext={trackingContext}
            trackingSource={trackingSource}
          />
        );

      case 'CREATE':
        return createContent();
      case 'SELECT':
        if (lastAutoSavedObject) {
          return (
            <SaveToTripContent
              tripList={data.tripList}
              savedSet={data.saveSet}
              tripListBottom={loadingNode}
              onPaginate={loadMore}
              hasMore={hasMore}
              saveObject={lastAutoSavedObject}
              onMount={() => {
                logInteraction({ elementClick: { element: 'stat' } });
              }}
              onCreateTrip={() => {
                if (props.onSelectCreateTripClicked) {
                  props.onSelectCreateTripClicked();
                }
              }}
              tripAction="MOVE"
              onMovedToTrip={(trip: TripTypes.Trip, saved, isActive: boolean) => {
                props.statCallback('MOVE', 'EXISTING', trip, saved, isActive);
                closeModal();
              }}
              onRemovedFromTrip={(trip: TripTypes.Trip, saved, isActive: boolean) => {
                props.statCallback('REMOVE', 'EXISTING', trip, saved, isActive);
                closeModal();
              }}
              onError={props.onError}
              trackingContext={trackingContext}
              trackingSource={trackingSource}
            />
          );
        }
        return '';
      case 'SELECT_CREATE':
        if (lastSavedItem) {
          return (
            <div className={styles.modalPadding}>
              <CreateTripWithMoveItemModal
                gate={() => Promise.resolve() /* do not propagate arguments */}
                item={lastSavedItem}
                onMovedToCreatedTrip={(trip: TripTypes.Trip) => {
                  props.statCallback('MOVE', 'NEW', trip, true, true);
                  closeModal();
                }}
                canMakePublicTrip={data.canMakePublicTrip}
              />
            </div>
          );
        }
        return createContent();

      default:
        return '';
    }
  };

  return (
    <TripsImpression element={saveObject.type === 'bundleSave' ? 'copyTripModal' : 'saveTripModal'}>
      <Modal header={modalHeader} bodyProps={{ isFullBleed: true }} onClose={closeModal}>
        {loadingNode && splashLoad ? (
          <div className={styles.loadingContainer}>{loadingNode}</div>
        ) : (
          content()
        )}
      </Modal>
    </TripsImpression>
  );
};

export default STATModal;
